import { IsGTMLoad, ValueObject } from "./types";
import pick from "lodash.pick";
import mapKeys from "lodash/mapKeys";
import { memoryStorage } from "./storage";
import {
  COUNTRIES,
  REQUIRED_PERMISSION_COUNTRIES
} from "@RHCommerceDev/utils/constants";
import yn from "yn";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";
import { formatCurrency } from "@RHCommerceDev/utils/currencyHelpers";
import getCountryFromUrl from "@RHCommerceDev/utils/getCountryFromUrl";
import { countryNameMapper } from "@RHCommerceDev/resources/countries-config.json";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";

export const compare = (a: ValueObject, b: ValueObject): boolean =>
  JSON.stringify(Object.entries(a).sort()).toLowerCase() ===
  JSON.stringify(Object.entries(b).sort()).toLowerCase();

export const getAnalyticsAttrsFromProps = (props: ValueObject): ValueObject => {
  return pick(props, [
    "authoringName",
    "bannerName",
    "bannerType",
    "ctaName",
    "positionInfo"
  ]);
};

export const isOnProductPage = () =>
  !processEnvServer &&
  (location?.pathname?.includes("/products.jsp") ||
    location?.pathname?.includes("/pg"));

export const isOnSearchPage = () =>
  !processEnvServer && location?.pathname?.includes("/search");

export const getAnalyticsDataAttrsFromProps = (
  props: ValueObject
): ValueObject => {
  const data = getAnalyticsAttrsFromProps(props);
  return (
    data &&
    mapKeys(data, (v, key) => {
      return `data-${key.toLowerCase()}`;
    })
  );
};

export function isGDPRCompliantORCookieAccepted(): boolean {
  const env = useEnv();
  const country = getCountryFromUrl();
  if (processEnvServer) {
    return false;
  }
  try {
    const sessionData = memoryStorage.getItem("analytics-sessionDetails") || {};
    const akamaiCountryCode = sessionData?.rhUser?.akamaiCountryCode;
    const analyticsCookie = sessionData?.cookiePreferences?.analyticsCookie;
    const cookieRules = sessionData?.cookiePreferences?.cookieRules || country;

    const thirdPartyAnalyticsCookie =
      sessionData?.cookiePreferences?.thirdPartyAnalyticsCookie;
    const isAnalyticsAccepted = yn(env.FEATURE_AN_864_GOOGLE_CONSENT_MODE_V2)
      ? yn(thirdPartyAnalyticsCookie)
      : !!yn(analyticsCookie) && !!yn(thirdPartyAnalyticsCookie);
    const countryToCheck =
      akamaiCountryCode === "GB" ? akamaiCountryCode : cookieRules;
    const isCountryRequiresPermission = REQUIRED_PERMISSION_COUNTRIES?.includes(
      countryToCheck || ""
    );

    if (
      (isAnalyticsAccepted === undefined || isAnalyticsAccepted === null) &&
      (akamaiCountryCode === COUNTRIES.US || akamaiCountryCode === COUNTRIES.CA)
    ) {
      return true;
    }

    if (!isCountryRequiresPermission || !!isAnalyticsAccepted) {
      return true;
    }

    return false;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function isGTMLoaded(): IsGTMLoad {
  if (processEnvServer) {
    return {
      isGTMLoaded: false
    };
  }
  const isGTMLoad = memoryStorage.getItem("isGTMLoad");
  const isCountryChanged = memoryStorage.getItem("isCountryChanged");
  const isGTMLoaded = Boolean(sessionStorage.getItem("isGTMLoaded"));

  if (isCountryChanged) {
    return {
      isGTMLoaded: false
    };
  }

  if (isGTMLoaded || isGTMLoad || isGDPRCompliantORCookieAccepted()) {
    return {
      isGTMLoaded: true
    };
  }

  return {
    isGTMLoaded: false
  };
}

export function getPriceData(
  userType,
  pricing,
  normalPriceKey,
  inStockProductCard
) {
  const price = Array.isArray(pricing?.[normalPriceKey])
    ? pricing?.[normalPriceKey]?.[0] ?? 0
    : pricing?.[normalPriceKey] ?? 0;
  const priceData =
    userType === "TRADE" || userType === "CONTRACT"
      ? {
          price:
            userType === "TRADE" ? pricing?.tradePrice : pricing?.contractPrice,
          type: userType?.toLowerCase()
        }
      : pricing?.salePrice && pricing?.salePrice < price
      ? { price: pricing?.salePrice, type: "sale" }
      : {
          price: price,
          type: normalPriceKey === "listPrice" ? "regular" : "member"
        };
  const stockOrSale = inStockProductCard
    ? pricing?.onSale
      ? "OnSale"
      : "InStock"
    : "Standard";
  return { priceData, stockOrSale };
}

export const getGACookieObject = () => {
  const gaCookieRegx = /^_g/;
  const cookiesObject = document.cookie
    ?.split(";")
    .reduce((cookies, cookie) => {
      const [name, value] = cookie.split("=").map(c => c.trim());
      if (gaCookieRegx.test(name)) {
        cookies[name] = decodeURIComponent(value);
      }
      return cookies;
    }, {});
  return cookiesObject;
};

export const preConfigureProduct = ({
  fullSkuId,
  preConfiguredSku,
  relatedProductSkuId
}) => {
  let productConfig = "";
  if (fullSkuId?.startsWith("M")) {
    productConfig = "MSKU";
  } else if (
    (fullSkuId && fullSkuId === preConfiguredSku) ||
    relatedProductSkuId
  ) {
    productConfig = "Preconfigured";
  }
  return productConfig;
};

export const formatShippingValue = ({
  freightOptions,
  pageContent,
  currencyCode,
  value
}) => {
  const { ground = 0, second = 0, next = 0 } = freightOptions;
  const groundPrice = ground
    ? formatCurrency(ground / 100, currencyCode, true)
    : pageContent?.FREE;
  const secondPrice = formatCurrency(second / 100, currencyCode, true);
  const nextPrice = formatCurrency(next / 100, currencyCode, true);
  const shippingValue = {
    ground: `${pageContent?.THREE_SEVEN_DAYS} ${groundPrice}`,
    second: `${pageContent?.TWO_DAYS} ${secondPrice}`,
    next: `${pageContent?.ONE_DAY} ${nextPrice}`
  };
  if (!processEnvServer) {
    memoryStorage.setItem("selectedValue", shippingValue[value || "ground"]);
  }
};

export const calculateTotalPrice = (lineitems: LineItemType[] = []) => {
  return lineitems?.reduce(
    (acc, cur) => acc + (cur?.price?.totalPrice || 0),
    0
  );
};

export const transformItem = ({
  detail,
  page,
  items,
  hasMemberPrice = false,
  rhUser,
  item_list_name
}) => {
  const itemPriceKey = hasMemberPrice ? "memberPrices" : "listPrices";
  const normalPriceKey = hasMemberPrice ? "memberPrice" : "listPrice";
  const relatedProductSku = memoryStorage.getItem("relatedProductSku");
  const fullSkuIdFromParam = detail.data.location?.paramMap?.fullSkuId;

  const title =
    (!processEnvServer && localStorage.getItem("analytics-category-title")) ||
    "";
  const productData: Product = !processEnvServer
    ? JSON.parse(localStorage.getItem("analytics-product-data") || "{}")
    : {};
  const urlProductId = detail.data.location?.pathname?.match(/prod(\d+)/);
  const productId =
    detail.data.location?.paramMap?.productId || urlProductId?.[0];

  const isShoppingCart = page?.url.includes("shopping_cart.jsp");
  const isPreconfigureSkuEvents =
    (detail.type.includes("order-placed") ||
      detail.type.includes("view-item") ||
      detail.type.includes("add-to-cart")) &&
    !detail.type.includes("view-item-list");

  return items?.map(item => {
    let priceKey = item?.price ? itemPriceKey : normalPriceKey;
    const itemPrice = item?.pricing || item?.price;
    const { priceData } = getPriceData(
      rhUser?.userType,
      itemPrice,
      priceKey,
      item?.inStockProductCard
    );
    const itemPriceData = Array.isArray(priceData?.price)
      ? priceData?.price?.[0]
      : priceData?.price;
    let price = 0;
    if (!itemPriceData) {
      priceKey = hasMemberPrice ? "memberPrice" : "itemPrice";
      if (rhUser?.userType === "TRADE") {
        price = (itemPrice?.tradePrice ?? itemPrice?.itemPrice) / 100;
      } else if (rhUser?.userType === "CONTRACT") {
        price = (itemPrice?.contractPrice ?? itemPrice?.itemPrice) / 100;
      } else {
        const salePrice = itemPrice?.salePrice;
        const listOrMemberPrice = itemPrice?.[priceKey];
        price =
          salePrice != null && salePrice < listOrMemberPrice
            ? salePrice / 100
            : listOrMemberPrice / 100;
      }
    }

    memoryStorage.removeItem("fromNav");

    const isRHMembership = item?.displayName === "RH Members Program";
    const item_id =
      !item?.productId && !item?.id && isRHMembership
        ? item?.displayName
        : item?.productId || item?.id;

    if (isRHMembership) {
      item_list_name = "Shopping Cart -> Shopping Cart";
    }

    const relatedProductSkuId = relatedProductSku?.includes(
      item?.sku?.toString()
    );

    const productConfig =
      item?.productConfig ||
      preConfigureProduct({
        fullSkuId: item?.sku,
        preConfiguredSku: fullSkuIdFromParam,
        relatedProductSkuId
      });

    if (!processEnvServer) {
      memoryStorage.removeItem("panelLineItem");
    }
    const country = getCountryFromUrl();
    const currency = countryNameMapper[country || "US"];
    const item_price = item?.pricing || itemPriceData || price;
    const finalPrice = item?.cartLineItem ? item_price / 100 : item_price;
    return {
      item_id,
      currency,
      item_name: item?.product?.displayName || item?.displayName || item?.name,
      item_variant: item?.sku ?? null,
      quantity: item?.quantity,
      price: finalPrice,
      item_category:
        item?.category || item?.product?.category || isShoppingCart
          ? null
          : title || productData?.category,
      item_list_name: detail.data.local?.item_list_name ? item_list_name : null,
      primaryProduct: !productId
        ? "-"
        : productId === item?.productId || productId === item?.id,
      ...(isPreconfigureSkuEvents ? { productConfig } : {}),
      ...(item?.atc_method ? { atc_method: item?.atc_method } : {})
    };
  });
};
